import { create } from 'zustand'

interface LeadLoadingStates {
  isDemoLoading: boolean
  isNextConversationLoading: boolean
  isWaitingForCallReport: boolean
}

interface DemoLoaderStore {
  leadLoadingStates: Record<number, LeadLoadingStates>

  setDemoLoading: (leadId: number, loading: boolean) => void
  setNextConversationLoading: (leadId: number, loading: boolean) => void
  setWaitingForCallReport: (leadId: number, waiting: boolean) => void
  
  getDemoLoading: (leadId: number) => boolean
  getNextConversationLoading: (leadId: number) => boolean
  getWaitingForCallReport: (leadId: number) => boolean
  

  clearAllLoadingStates: (leadId: number) => void
  getLoadingStates: (leadId: number) => LeadLoadingStates
  clearLeadLoading: (leadId: number) => void
  
  hasAnyActiveLoading: () => boolean
  getActiveLoadingLeads: () => number[]
}
const getDefaultLoadingStates = (): LeadLoadingStates => ({
  isDemoLoading: false,
  isNextConversationLoading: false,
  isWaitingForCallReport: false
})

const ensureLeadLoadingData = (state: any, leadId: number): LeadLoadingStates => {
  if (!state.leadLoadingStates[leadId]) {
    return getDefaultLoadingStates()
  }
  return state.leadLoadingStates[leadId]
}

export const useDemoLoaderStore = create<DemoLoaderStore>((set, get) => ({
  leadLoadingStates: {},

  setDemoLoading: (leadId: number, loading: boolean) => {
    console.log(`Setting demo loading for lead ${leadId}: ${loading}`)
    set((state) => {
      const existingStates = ensureLeadLoadingData(state, leadId)
      return {
        leadLoadingStates: {
          ...state.leadLoadingStates,
          [leadId]: {
            ...existingStates,
            isDemoLoading: loading
          }
        }
      }
    })
  },

  setNextConversationLoading: (leadId: number, loading: boolean) => {
    console.log(`Setting next conversation loading for lead ${leadId}: ${loading}`)
    set((state) => {
      const existingStates = ensureLeadLoadingData(state, leadId)
      return {
        leadLoadingStates: {
          ...state.leadLoadingStates,
          [leadId]: {
            ...existingStates,
            isNextConversationLoading: loading
          }
        }
      }
    })
  },

  setWaitingForCallReport: (leadId: number, waiting: boolean) => {
    console.log(`Setting waiting for call report for lead ${leadId}: ${waiting}`)
    set((state) => {
      const existingStates = ensureLeadLoadingData(state, leadId)
      return {
        leadLoadingStates: {
          ...state.leadLoadingStates,
          [leadId]: {
            ...existingStates,
            isWaitingForCallReport: waiting
          }
        }
      }
    })
  },

  getDemoLoading: (leadId: number) => {
    const state = get()
    const loadingStates = ensureLeadLoadingData(state, leadId)
    return loadingStates.isDemoLoading
  },

  getNextConversationLoading: (leadId: number) => {
    const state = get()
    const loadingStates = ensureLeadLoadingData(state, leadId)
    return loadingStates.isNextConversationLoading
  },

  getWaitingForCallReport: (leadId: number) => {
    const state = get()
    const loadingStates = ensureLeadLoadingData(state, leadId)
    return loadingStates.isWaitingForCallReport
  },

  clearAllLoadingStates: (leadId: number) => {
    console.log(`Clearing all loading states for lead ${leadId}`)
    set((state) => ({
      leadLoadingStates: {
        ...state.leadLoadingStates,
        [leadId]: getDefaultLoadingStates()
      }
    }))
  },

  getLoadingStates: (leadId: number) => {
    const state = get()
    return ensureLeadLoadingData(state, leadId)
  },

  clearLeadLoading: (leadId: number) => {
    console.log(`Clearing all loading data for lead ${leadId}`)
    set((state) => {
      const newLoadingStates = { ...state.leadLoadingStates }
      delete newLoadingStates[leadId]
      return { leadLoadingStates: newLoadingStates }
    })
  },

  hasAnyActiveLoading: () => {
    const state = get()
    return Object.values(state.leadLoadingStates).some(
      (states: LeadLoadingStates) => 
        states.isDemoLoading || 
        states.isNextConversationLoading || 
        states.isWaitingForCallReport
    )
  },

  getActiveLoadingLeads: () => {
    const state = get()
    return Object.entries(state.leadLoadingStates)
      .filter(([, states]) => 
        states.isDemoLoading || 
        states.isNextConversationLoading || 
        states.isWaitingForCallReport
      )
      .map(([leadId]) => parseInt(leadId))
  }
}))